import React, { useMemo } from 'react';
import StandardQuickFilters from '../StandardComponents/StandardQuickFilters/StandardQuickFilters';
import SourceIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import BlockIcon from '@mui/icons-material/Block';

const SOURCE_OPTIONS = ['stripe', 'manual', 'code'];
const STATUS_OPTIONS = ['active', 'pending', 'expired', 'revoked'];

const defaultColors = {
  active: { bg: '#EFF6FF', text: '#1E40AF', border: '#3B82F6' },
  default: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
  hover: { bg: '#EFF6FF', text: '#1E40AF', border: '#3B82F6' },
};

const statusColors = {
  active: {
    active: { bg: '#EBFFF8', text: '#065F46', border: '#00CC85' },
    default: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
    hover: { bg: '#EBFFF8', text: '#065F46', border: '#00CC85' },
  },
  pending: {
    active: { bg: '#FFFBEB', text: '#92400E', border: '#F59E0B' },
    default: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
    hover: { bg: '#FFFBEB', text: '#92400E', border: '#F59E0B' },
  },
  expired: {
    active: { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' },
    default: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
    hover: { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' },
  },
  revoked: {
    active: { bg: '#FEF2F2', text: '#991B1B', border: '#EF4444' },
    default: { bg: '#F9FAFB', text: '#374151', border: '#E5E7EB' },
    hover: { bg: '#FEF2F2', text: '#991B1B', border: '#EF4444' },
  },
};

const statusIcon = (status) => {
  switch (status) {
    case 'active': return <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#00CC85' }} />;
    case 'pending': return <ScheduleIcon sx={{ fontSize: 18, color: '#F59E0B' }} />;
    case 'expired': return <HistoryIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />;
    case 'revoked': return <BlockIcon sx={{ fontSize: 18, color: '#EF4444' }} />;
    default: return null;
  }
};

const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * EntitlementsQuickFilters — Source + status quick filters for billing entitlements lists.
 * Uses StandardQuickFilters per D-list-views standards.
 */
export default function EntitlementsQuickFilters({
  entitlements = [],
  rowStatus,
  activeFilters = {},
  onApplyFilter,
}) {
  const filters = useMemo(() => {
    const sourceFilters = SOURCE_OPTIONS.map((src) => ({
      label: titleCase(src),
      field: 'source',
      value: src,
      count: entitlements.filter((r) => r.source === src).length,
      colors: defaultColors,
      icon: <SourceIcon sx={{ fontSize: 18, color: '#3B82F6' }} />,
    }));

    const statusFilters = STATUS_OPTIONS.map((st) => ({
      label: titleCase(st),
      field: 'status',
      value: st,
      count: entitlements.filter((r) => rowStatus(r) === st).length,
      colors: statusColors[st] || defaultColors,
      icon: statusIcon(st),
    }));

    return [...sourceFilters, ...statusFilters];
  }, [entitlements, rowStatus]);

  const handleApplyFilter = (filter) => {
    onApplyFilter(filter);
  };

  return (
    <StandardQuickFilters
      title="Quick Filters"
      filters={filters}
      activeFilters={activeFilters}
      onApplyFilter={handleApplyFilter}
    />
  );
}
